import { CREDENTIAL_FORM_DESCRIPTORS } from "@api/shared-runtime/credential-form-descriptors";
import {
  credentialImportFields,
  type CredentialImportVersion,
} from "@api/shared-runtime/credential-import-fields";
import type { CredentialType } from "@api/shared-runtime/enums";
import { PERSONAL_INFO_FIELD_GROUPS } from "../components/credential-vault-edit-modal/personal-info-fields";

export type ImportVersion = CredentialImportVersion;

export type ImportComparisonRow = {
  path: string;
  label: string;
  before: string;
  after: string;
  changed: boolean;
  cleared: boolean;
};

const COLUMN_LABELS: Record<string, string> = {
  number: "Number",
  stateCode: "State",
  issuedAt: "Issue date",
  expiresAt: "Expiration date",
  status: "Status",
};

function humanize(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function formatImportValue(value: unknown): string {
  if (value === undefined) {
    return "Not provided";
  }
  if (value === null) {
    return "No value";
  }
  if (value === "") {
    return "Blank";
  }
  if (value === true) {
    return "Yes";
  }
  if (value === false) {
    return "No";
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "Empty list";
    }
    if (value.some((item) => item !== null && typeof item === "object")) {
      return value
        .map((item, index) => `Item ${index + 1}: ${formatImportValue(item)}`)
        .join("\n");
    }
    return value.map(formatImportValue).join(", ");
  }
  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return "Empty object";
    }
    return entries
      .map(([key, item]) => `${humanize(key)}: ${formatImportValue(item)}`)
      .join("; ");
  }
  return String(value);
}

function isEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (typeof value === "object" && Object.keys(value).length === 0)
  );
}

function fieldLabels(type: CredentialType) {
  const labels = new Map(Object.entries(COLUMN_LABELS));
  for (const field of CREDENTIAL_FORM_DESCRIPTORS[type].fields) {
    const path =
      field.source === "details" ? `details.${field.key}` : field.key;
    labels.set(path, field.label);
  }
  for (const group of PERSONAL_INFO_FIELD_GROUPS) {
    labels.set(`details.${group.key}`, group.label);
    for (const field of group.fields) {
      labels.set(`details.${group.key}.${field.key}`, field.label);
    }
  }
  return labels;
}

function pathLabel(path: string, labels: Map<string, string>): string {
  const exact = labels.get(path);
  if (exact) {
    return exact;
  }
  const segments = path.replace(/^details\./, "").split(".");
  return segments
    .map((part) =>
      /^\d+$/.test(part)
        ? `Item ${Number(part) + 1}`
        : humanize(decodeURIComponent(part)),
    )
    .join(" · ");
}

/** Compare the stored records directly. Edit forms intentionally omit fields
 * and normalize empty answers, so their serializers cannot describe replacement.
 * Shared field boundaries keep selections aligned with server-side merging. */
export function providerImportComparison({
  credentialType,
  sourceVersion,
  targetVersion,
}: {
  credentialType: CredentialType;
  sourceVersion: ImportVersion;
  targetVersion: ImportVersion | null;
}): ImportComparisonRow[] {
  const labels = fieldLabels(credentialType);
  return credentialImportFields({ sourceVersion, targetVersion }).map(
    ({ path, before, after, changed }) => ({
      path,
      label: pathLabel(path, labels),
      before: formatImportValue(before),
      after: formatImportValue(after),
      changed,
      cleared: changed && !isEmpty(before) && isEmpty(after),
    }),
  );
}
